/**
 * Who the picture guide is for, and their jobs.
 *
 * The cashier comes first and is the default: most people who open the guide
 * are standing at the till. A job's id is unique across every group, so
 * /help/<job> needs no group in the address.
 */

import { Boxes, Crown, ShoppingBasket, UserCog } from "lucide-react";
import type { ComponentType } from "react";

import { MANAGER_TASKS, OWNER_TASKS, STOCK_TASKS } from "./roles";
import type { Tone } from "./scene";
import { TASKS as CASHIER_TASKS, type Task, type Words } from "./tasks";

export interface Group {
  id: string;
  title: Words;
  /** Who it is, in a few words, under the title. */
  who: Words;
  icon: ComponentType<{ className?: string }>;
  tone: Tone;
  tasks: Task[];
}

export const GROUPS: Group[] = [
  {
    id: "cashier",
    title: { en: "Cashier" },
    who: { en: "At the till" },
    icon: ShoppingBasket,
    tone: "money",
    tasks: CASHIER_TASKS,
  },
  {
    id: "manager",
    title: { en: "Manager" },
    who: { en: "Runs the shop floor" },
    icon: UserCog,
    tone: "brand",
    tasks: MANAGER_TASKS,
  },
  {
    id: "stock",
    title: { en: "Stock" },
    who: { en: "Deliveries and shelves" },
    icon: Boxes,
    tone: "gold",
    tasks: STOCK_TASKS,
  },
  {
    id: "owner",
    title: { en: "Owner" },
    who: { en: "Staff, tills, money" },
    icon: Crown,
    tone: "navy",
    tasks: OWNER_TASKS,
  },
];

export const ALL_TASKS: Task[] = GROUPS.flatMap((g) => g.tasks);

export function findGroup(id: string | null | undefined): Group {
  return GROUPS.find((g) => g.id === id) ?? GROUPS[0]!;
}

export function findTask(id: string | undefined): { task: Task; group: Group } | undefined {
  for (const group of GROUPS) {
    const task = group.tasks.find((t) => t.id === id);
    if (task) return { task, group };
  }
  return undefined;
}
